/**
 * Subscription catalog + purchased subs: `portion` vs persons (legacy v2 + list parity).
 */
import type { SubscriptionData } from '../types';

const PORTION_EPS = 0.001;

export function portionsEqual(a: number, b: number): boolean {
  return Math.abs(a - b) < PORTION_EPS;
}

export function parsePortion(raw: unknown): number | null {
  if (raw == null || raw === '') return null;
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (Number.isNaN(n) || n <= 0) return null;
  return n;
}

export function getCatalogPlanPortion(plan: Record<string, unknown> | null | undefined): number | null {
  if (!plan || typeof plan !== 'object') return null;
  const detail = plan.detail as Record<string, unknown> | undefined;
  const subscription_detail = plan.subscription_detail as Record<string, unknown> | undefined;
  const particulars_macros = plan.particularsMacros as Record<string, unknown> | undefined;

  return (
    parsePortion(plan.portion) ??
    parsePortion(plan.portions) ??
    parsePortion(plan.portion_size) ??
    parsePortion(detail?.portion) ??
    parsePortion(subscription_detail?.portion) ??
    parsePortion(particulars_macros?.portion) ??
    parsePortion(plan.no_of_people) ??
    parsePortion(plan.persons) ??
    parsePortion(plan.person_count) ??
    parsePortion(plan.number_of_people) ??
    parsePortion(plan.people)
  );
}

const DEFAULT_MEMBER_PORTIONS = [1, 2, 3, 4];

export function collectPortionOptionsFromRegularPlans(regular_plans: unknown[]): number[] {
  const set = new Set<number>();
  for (const cat of regular_plans ?? []) {
    const subs = (cat as { subscriptions?: unknown[] })?.subscriptions ?? [];
    for (const sub of subs) {
      const type_data = (sub as { type_data?: unknown[] })?.type_data ?? [];
      for (const plan of type_data) {
        const p = getCatalogPlanPortion(plan as Record<string, unknown>);
        if (p != null) set.add(p);
      }
    }
  }
  if (set.size === 0) return [...DEFAULT_MEMBER_PORTIONS];
  return Array.from(set).sort((a, b) => a - b);
}

export function formatPortionAsPersonsLabel(portion: number): string {
  const rounded_tenth = Math.round(portion * 10) / 10;
  if (Math.abs(rounded_tenth - Math.round(rounded_tenth)) < PORTION_EPS) {
    return String(Math.round(rounded_tenth));
  }
  return String(rounded_tenth);
}

/** True when count is exactly 1 (person); otherwise use "people". */
export function isSinglePersonCount(count: number): boolean {
  const n = parsePortion(count) ?? count;
  return Math.abs(n - 1) < PORTION_EPS;
}

/** e.g. "1 person" or "2 people", or portion mode when `portion_text` is `portion`. */
export function formatPersonCountLabel(
  count: number,
  t: (key: string) => string,
  portion_text_mode: 'portion' | 'person' = 'person',
): string {
  const num = formatPortionAsPersonsLabel(count);
  if (portion_text_mode === 'portion') {
    const word = isSinglePersonCount(count) ? t('subscriptions.portion') : t('subscriptions.portions');
    return `${num} ${word}`;
  }
  const word = isSinglePersonCount(count) ? t('subscriptions.person') : t('subscriptions.people');
  return `${num} ${word}`;
}

export function getSubscriptionPortionAsPersons(sub: SubscriptionData): number {
  const detail = sub.detail as Record<string, unknown> | undefined;
  const pm = sub.particularsMacros;
  const sub_detail = sub.subscription_detail as Record<string, unknown> | undefined;
  const root = sub as unknown as Record<string, unknown>;

  const from_portion =
    parsePortion(detail?.portion) ??
    parsePortion(pm?.portion) ??
    parsePortion(sub_detail?.portion) ??
    parsePortion(root.portion);

  if (from_portion != null) return from_portion;

  const legacy =
    parsePortion(detail?.persons) ??
    parsePortion(detail?.person_count) ??
    (pm?.no_of_people != null ? parsePortion(pm.no_of_people) : null) ??
    (pm?.people != null ? parsePortion(pm.people) : null) ??
    (pm?.number_of_people != null ? parsePortion(pm.number_of_people) : null);

  if (legacy != null) return legacy;

  const mp = detail?.maximum_protein;
  if (mp != null) {
    const n = Number(mp);
    if (!Number.isNaN(n) && n > 0) return Math.round(n);
  }
  return 1;
}

export function catalogPlanMatchesSelectedPortion(plan: Record<string, unknown>, selected_portion: number): boolean {
  const p = getCatalogPlanPortion(plan);
  if (p != null) return portionsEqual(p, selected_portion);
  return portionsEqual(selected_portion, 1);
}
